import React, { useEffect, useState } from 'react';
import { Save, FileSearch } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import { capaInvestigationsService } from '../../services/capaInvestigationsService';
import { userService, User } from '../../services/userService';
import { filterPickableUsers } from '../../utils/userPickable';
import extractApiError from '../../utils/apiError';

const userLabel = (u: User) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    if (name && u.login_id) return `${name} (${u.login_id})`;
    return name || u.login_id || `#${u.id}`;
};

interface Props {
    isOpen: boolean;
    onClose: () => void;
    issueId: number;
    issueCode?: string;
    issueName?: string;
    // For re-investigate: pre-fill owner with the closed investigation's owner_user_id
    presetOwnerId?: number | null;
    onCreated?: (investigationId: number) => void;
}

const SendToInvestigationModal: React.FC<Props> = ({
    isOpen, onClose, issueId, issueCode, issueName, presetOwnerId, onCreated,
}) => {
    const [users, setUsers] = useState<User[]>([]);
    const [ownerId, setOwnerId] = useState<number | null>(null);
    const [dueDate, setDueDate] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setOwnerId(presetOwnerId ?? null);
        setDueDate('');
        userService.getAllUsers({ limit: 200 }).then(r => setUsers(filterPickableUsers(r.data || []))).catch(() => {});
    }, [isOpen, presetOwnerId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!ownerId) {
            setToast({ type: 'error', message: 'Owner is required' });
            return;
        }
        try {
            setSubmitting(true);
            const inv = await capaInvestigationsService.sendToInvestigation(issueId, {
                owner_user_id: ownerId,
                due_date: dueDate || null,
            });
            setToast({ type: 'success', message: 'Investigation created.' });
            onCreated?.(inv.id);
            onClose();
        } catch (err: any) {
            setToast({ type: 'error', message: extractApiError(err, 'Failed to create investigation') });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="md">
                <Modal.Header>
                    <span className="flex items-center gap-2">
                        <FileSearch size={16} />
                        Send to Investigation
                    </span>
                </Modal.Header>
                <Modal.Body>
                    {(issueCode || issueName) && (
                        <div className="text-xs text-gray-500 mb-3">
                            Issue: <span className="font-medium text-gray-800">{issueCode}</span> {issueName && <>— {issueName}</>}
                        </div>
                    )}
                    <form id="send-to-investigation-form" onSubmit={handleSubmit} className="grid grid-cols-2 gap-x-6 gap-y-4" data-testid="MOD_capa_send_investigation_form">
                        <div className="col-span-2">
                            <FormControl.SearchSelect
                                label="Owner"
                                required
                                placeholder="— pick an owner —"
                                options={users.map(u => ({ value: u.id, label: userLabel(u) }))}
                                value={ownerId}
                                onChange={v => setOwnerId(v ? Number(v) : null)}
                                clearable
                                data-testid="SEL_capa_send_investigation_owner"
                            />
                        </div>
                        <div className="col-span-2">
                            <FormControl.DatePicker
                                label="Due Date (optional)"
                                value={dueDate}
                                onChange={e => setDueDate(e.target.value)}
                                data-testid="INP_capa_send_investigation_due_date"
                            />
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        type="submit"
                        form="send-to-investigation-form"
                        variant="fill"
                        icon={Save}
                        disabled={submitting || !ownerId}
                        data-testid="BTN_capa_send_investigation_submit"
                    >
                        {submitting ? 'Creating...' : 'Create Investigation'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default SendToInvestigationModal;
